import { useEffect, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { Check } from "lucide-react";
import { FONT, WARM } from "@shared/brand";

type SaveState = "idle" | "saving" | "saved" | "error";

interface SectionSaveStatusProps {
  isPending: boolean;
  isError: boolean;
  lastSavedAt: Date | null;
  onRetry?: () => void;
}

/**
 * Section-level save status indicator. Mounts in the section header, right-aligned.
 * Announces state changes to screen readers via aria-live="polite".
 *
 * States:
 *   idle      — "All changes saved" (or empty before first save)
 *   saving    — "Saving…"
 *   saved     — "Saved just now" (transitions to idle after 5s)
 *   error     — "Reconnecting…" + Retry button
 *
 * Shows a one-time toast on first successful save per session.
 */
export function SectionSaveStatus({ isPending, isError, lastSavedAt, onRetry }: SectionSaveStatusProps) {
  const { toast } = useToast();
  const hasToastedRef = useRef(false);
  const [displayState, setDisplayState] = useState<SaveState>("idle");

  useEffect(() => {
    if (isPending) {
      setDisplayState("saving");
      return;
    }
    if (isError) {
      setDisplayState("error");
      return;
    }
    if (lastSavedAt) {
      setDisplayState("saved");

      if (!hasToastedRef.current) {
        hasToastedRef.current = true;
        toast({
          description: (
            <span className="flex items-center gap-2">
              <Check className="h-4 w-4" style={{ color: "#1ABC9C" }} />
              Saved.
            </span>
          ) as any,
          duration: 2500,
        });
      }

      const timer = setTimeout(() => setDisplayState("idle"), 5000);
      return () => clearTimeout(timer);
    }
  }, [isPending, isError, lastSavedAt, toast]);

  const label = (() => {
    switch (displayState) {
      case "saving": return "Saving…";
      case "saved":  return "Saved just now";
      case "error":  return "Reconnecting…";
      default:       return lastSavedAt ? "All changes saved" : "";
    }
  })();

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="flex items-center gap-2"
      style={{ fontFamily: FONT.sans, fontSize: 13, color: WARM.taupe }}
    >
      {label && <span>{label}</span>}
      {displayState === "error" && onRetry && label && (
        <button
          type="button"
          onClick={onRetry}
          style={{ fontSize: 13, color: WARM.taupe, textDecoration: "underline", background: "none", border: "none", cursor: "pointer", padding: 0 }}
        >
          Retry
        </button>
      )}
    </div>
  );
}
